import React, {
  useEffect, useLayoutEffect, useMemo, useState,
} from 'react';
import _ from 'lodash';
import Chart from 'react-apexcharts';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import utils from '../../../utils/utils';
import Loader from '../../_common/Loader/Loader';
import { useQuery } from '../../../utils/hooks/useQuery';
import { getSelectRepListRequest } from '../../../store/actions/users';
import classes from './dashboard.module.scss';
import { getDashboardChartRequest, getDashboardStatsRequest } from '../../../store/actions/dashboard';
import DashboardStats from './DashboardStats/DashboardStats';
import { RangeDatePicker } from '../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import Select from '../../_common/Form/Selects/Select/Select';
import Button from '../../_common/Form/Buttons/Button';
import { changeMenuPath } from '../../../store/actions/menu';
import DashboardFooter from './DashboardFooter';
import accountHelpers from '../../../utils/accountHelpers';
import FilterButton from '../../_common/FilterButton/FilterButton';

const chartTemplate = {
  options: {
    chart: {
      height: 350,
      type: 'bar',
      toolbar: {
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      events: {},
    },

    legend: {
      show: false,
    },

    dataLabels: {
      enabled: false,
    },

    colors: ['#547EEE', '#E905E0'],

    fill: {
      opacity: 1,
    },

    plotOptions: {
      bar: {
        // borderRadius: 4,
        horizontal: false,
        columnWidth: '50%',
      },
    },

    grid: {
      show: true,
      strokeDashArray: 2,
      row: {
        // colors: ['#fff', '#f2f2f2']
      },
    },

    tooltip: {
      style: {
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
      },
      y: {
        formatter(val) {
          const formatter = Intl.NumberFormat('en');
          return formatter.format(val);
        },
      },
    },

    xaxis: {
      type: 'datetime',
      categories: [],
      // position: 'top',
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        fill: {
          type: 'gradient',
          gradient: {
            colorFrom: '#D8E3F0',
            colorTo: '#BED1E6',
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
      },
      tooltip: {
        enabled: false,
      },
    },

    yaxis: {
      labels: {
        style: {
          colors: ['rgba(29, 39, 64, 0.5)'],
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          cssClass: 'apexcharts-yaxis-label',
        },
        // show: false,
        formatter(val) {
          const formatter = Intl.NumberFormat('en', { notation: 'compact' });
          return formatter.format(val);
        },
      },
    },
  },

  series: [{
    name: '',
    data: [],
  }],
};

const rangeTypes = ['day', 'week', 'month'];

const chartTypes = [
  {
    title: 'Total Sales',
    key: 'revenue',
    value: 'revenue',
    width: 90,
  },
  {
    title: 'Profit',
    key: 'profit',
    value: 'profit',
    width: 90,
  },
  {
    title: 'Prescription Count',
    key: 'sales',
    value: 'sales',
    width: 150,
  },
  {
    title: 'Margin',
    key: 'margin',
    value: 'margin',
    width: 90,
  },
  {
    title: 'Cost',
    key: 'cost',
    value: 'cost',
    width: 90,
  },
];

const collectRequestData = (requestData) => {
  const data = utils.deleteEmptyKeys(requestData);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).add(1, 'day').format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).add(1, 'day').format('YYYY-MM-DD');
    }
  }

  return data;
};

let firstLoad = true;
const limit = 20;

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

const Dashboard = () => {
  const isAdmin = accountHelpers.isInOneOfRoles(['Admin', 'Pharmacy']);
  const isHealth = accountHelpers.isInOneOfRoles(['Health', 'Manufacturer Users']);
  const isRep = accountHelpers.isInOneOfRoles(['Rep']);
  const showDetailsBtn = accountHelpers.isInOneOfRoles(['Admin', 'Health']);

  const hasProfitAccess = accountHelpers.isInRole('Health')
    && accountHelpers.getUser().healthUser?.[0].canSeeProfit;

  const dispatch = useDispatch();
  const { query, setQuery, queryToStringify } = useQuery();
  const { pathname } = useLocation();

  const { selectRepList, pageCount, currentPage } = useSelector((store) => store.users);
  const { dashboardChart } = useSelector((store) => store.dashboard);

  const [chart, setChart] = useState(chartTemplate);

  const [chartType, setChartType] = useState(query.chartType || 'revenue');
  const [range, setRange] = useState(query.range || 'day');

  const [requestData, setRequestData] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
    rep: query.rep || '',
  });
  const [dashboardsLoading, setDashboardsLoading] = useState(false);
  const [chartLoading, setChartsLoading] = useState(false);

  useLayoutEffect(() => () => firstLoad = true, []);

  useEffect(() => {
    if (firstLoad) {
      dispatch(getSelectRepListRequest({ limit, role: 'Rep' }));
    }

    (async () => {
      setDashboardsLoading(true);

      const data = collectRequestData(requestData);

      await dispatch(getDashboardStatsRequest({ ...data }));

      await dispatch(getDashboardChartRequest({ ...data, range }));

      setDashboardsLoading(false);

      firstLoad = false;
    })();
  }, [requestData]);

  useEffect(() => {
    if (!firstLoad) {
      const newQuery = utils.deleteEmptyKeys({ ...requestData, chartType, range });

      dispatch(changeMenuPath('Dashboard', `${pathname}${queryToStringify(newQuery)}`));
      setQuery(newQuery);
    }
  }, [requestData, chartType, range]);

  useEffect(() => {
    if (!firstLoad) {
      (async () => {
        setChartsLoading(true);
        const data = collectRequestData(requestData);

        await dispatch(getDashboardChartRequest({ ...data, range }));
        setChartsLoading(false);
      })();
    }
  }, [range]);

  useEffect(() => {
    (async () => {
      setChartsLoading((prev) => !prev);
      const newChart = _.cloneDeep(chartTemplate);
      newChart.options.xaxis.categories = dashboardChart?.map((d) => d.start_date) || [];
      newChart.series[0].name = _.upperFirst(chartType);
      newChart.series[0].data = dashboardChart?.map(
        (d) => (d[chartType].toString().includes('Infinity') ? 0 : d[chartType]),
      ) || [];

      if (chartType === 'cost') {
        _.set(newChart, 'series[1].name', 'Margin');
        _.set(newChart, 'series[1].data', dashboardChart?.map(
          (d) => (d.margin.toString().includes('Infinity') ? 0 : d.profit),
        ) || []);
      }

      // newChart.options.chart.events.dataPointSelection = (event, chartContext, { seriesIndex, dataPointIndex }) => {
      //   // const { seriesIndex, dataPointIndex } = config;
      //
      //   const value = newChart.options.xaxis.categories[dataPointIndex];
      //
      //   console.log('range', range);
      //   console.log('seriesIndex', seriesIndex);
      //   console.log('columnIndex', dataPointIndex);
      //   console.log('value', value);
      // };

      await setChart(newChart);

      setChartsLoading((prev) => !prev);
    })();
  }, [dashboardChart, chartType]);

  const onChangeDate = (currentDate) => {
    setRequestData((prev) => ({ ...prev, start: currentDate[0], end: currentDate[1] }));
  };

  const nextOptionListRequest = async (page) => {
    await dispatch(getSelectRepListRequest({ page, limit, role: 'Rep' }));
  };

  const changeRep = (rep) => {
    setRequestData((prev) => ({ ...prev, rep }));
  };

  const clearFilters = () => {
    setRequestData({
      start: thisMonthStartDay,
      end: thisMonthEndDate,
      rep: '',
    });
  };

  const tabs = useMemo(() => chartTypes.filter(({ key }) => {
    if (hasProfitAccess) return key !== 'margin';

    if (isHealth) return key === 'revenue' || key === 'sales';

    if (isRep) return key !== 'profit';

    return true;
  }), [isHealth, isRep, hasProfitAccess]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.dashboard_header}>
        <div className={classes.header_wrapper}>
          <div className={classes.calendar}>
            <RangeDatePicker
              currentDate={requestData}
              onChangeDate={onChangeDate}
              showLastYear
            />
          </div>

          {isAdmin && (
            <div className={classes.dashboard_select_rep}>
              <Select
                label="All reps"
                nextOptionListRequest={nextOptionListRequest}
                options={selectRepList}
                totalPage={pageCount}
                currentPage={currentPage}
                limit={limit}
                currentOption={requestData.rep}
                onChange={changeRep}
                optionPathname="name"
              />
            </div>
          )}

          <div style={{ width: 160 }}>
            <Button
              title="Clear"
              clearButton
              onClick={clearFilters}
            />
          </div>

          {showDetailsBtn && (
            <Button
              className={classes.details_btn}
              title="PDC Statistics"
              href={`/dashboard/pdc-statistics${queryToStringify(query)}`}
              reverseColor
            />
          )}
        </div>
      </div>

      <DashboardStats />

      <div
        className={classes.dashboard_wrapper}
      >
        <div className={classes.dashboard_wrapper_filters}>
          <div className={classes.filter_wrapper}>
            {rangeTypes.map((type) => (
              <button
                type="button"
                key={type}
                className={`${classes.button_dashboard} ${range === type ? classes.active_button : ''}`}
                onClick={() => setRange(type)}
              >
                {type}
              </button>
            ))}
          </div>

          <div className={classes.filter_wrapper}>
            <FilterButton
              activeValue={chartType}
              buttonsData={tabs}
              onClick={(type) => setChartType(type)}
              height={40}
              fontWeight={600}
              disableActiveButton
            />
          </div>
        </div>

        <div className={classes.chart_wrapper}>
          {!dashboardsLoading && !chartLoading
            ? (chart.series[0].data.length
              ? <Chart options={chart.options} series={chart.series} type="bar" width="100%" height={270} />
              : <div className={classes.chart_is_empty}>No data to display</div>)
            : <Loader size={40} className={classes.chart_wrapper_loader} />}
        </div>
      </div>

      <DashboardFooter />
    </div>
  );
};

export default Dashboard;
